import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error(t("Password must be at least 8 characters.", "Le mot de passe doit comporter au moins 8 caractères.")); return; }
    if (pw !== confirm) { toast.error(t("Passwords don't match.", "Les mots de passe ne correspondent pas.")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success(t("Password updated — please sign in.", "Mot de passe mis à jour — connectez-vous."));
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="reset-page" className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-primary/60 hover:text-primary mb-8"><ArrowLeft size={16} /> {t("Back to sign in", "Retour à la connexion")}</Link>
        <h1 className="font-display text-4xl md:text-5xl font-medium text-primary">{t("Set a new password", "Nouveau mot de passe")}</h1>
        {!token ? (
          <p className="text-destructive mt-4" data-testid="reset-no-token">{t("This reset link is missing its token. Please request a new one.", "Ce lien de réinitialisation est invalide. Veuillez en demander un nouveau.")}</p>
        ) : (
          <form onSubmit={submit} className="space-y-5 mt-8" data-testid="reset-form">
            <div>
              <Label htmlFor="pw">{t("New password", "Nouveau mot de passe")}</Label>
              <Input id="pw" type="password" data-testid="reset-password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("Min. 8 characters", "Min. 8 caractères")} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="confirm">{t("Confirm password", "Confirmer le mot de passe")}</Label>
              <Input id="confirm" type="password" data-testid="reset-confirm" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 rounded-xl" />
            </div>
            <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />} {t("Update password", "Mettre à jour")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
